import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

const api = axios.create({
  baseURL: API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
    }
    if (error.response?.status === 403 || error.response?.data?.message?.includes('LIMIT_REACHED') || error.response?.data?.error?.includes('LIMIT_REACHED')) {
      // Broadcast limit reached event for UI to pick up (e.g. open Upgrade Modal)
      const message = error.response.data?.message || error.response.data?.error || '';
      if (message.includes('DAILY_LIMIT_REACHED')) {
        window.dispatchEvent(new CustomEvent('daily-limit-reached', { detail: { message: 'Today limit is over' } }));
      } else if (message.includes('Plan Limit') || message.includes('Upgrade') || message.includes('TOKEN_LIMIT_EXHAUSTED')) {
        window.dispatchEvent(new CustomEvent('plan-limit-reached', { detail: { message } }));
      }
    }
    return Promise.reject(error)
  }
)

export interface LoginCredentials {
  email: string
  password: string
  loginType?: 'user' | 'admin'
}

export interface SignupData {
  firstName: string
  lastName: string
  companyName: string
  contactNumber: string
  email: string
  password: string
  confirmPassword: string
}

export interface LoginResponse {
  success: boolean
  mfaRequired?: boolean
  mfa_token?: string
  message?: string
  data?: {
    token: string
    user: {
      id: string
      username: string
      email: string
      firstName: string
      lastName: string
      companyName: string
      workingDir: string
      subscribed?: 'F' | 'P' | 'S'
      role?: 'user' | 'admin'
      authProvider?: string
    }
  }
}

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  message?: string
}

export const authService = {
  async signup(data: SignupData): Promise<ApiResponse> {
    const response = await api.post('/auth/signup', data)
    return response.data
  },

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await api.post('/auth/login', credentials)
    return response.data
  },

  async verifyEmail(token: string): Promise<ApiResponse> {
    const response = await api.get(`/auth/verify-email/${token}`)
    return response.data
  },

  async resendVerification(email: string): Promise<ApiResponse> {
    const response = await api.post('/auth/resend-verification', { email })
    return response.data
  },

  async logout(): Promise<ApiResponse> {
    const response = await api.post('/auth/logout')
    return response.data
  },

  async verifyToken(): Promise<ApiResponse> {
    const response = await api.get('/auth/verify')
    return response.data
  },

  async getGoogleSignupUrl(): Promise<{ url: string }> {
    const response = await api.get('/auth/google/signup/url')
    return response.data
  },

  async completeGoogleSignup(data: SignupData): Promise<ApiResponse> {
    const response = await api.post('/auth/google/signup/complete', data)
    return response.data
  },

  async getGoogleLoginUrl(): Promise<{ url: string }> {
    const response = await api.get('/auth/google/login/url')
    return response.data
  },

  async forgotPassword(email: string): Promise<ApiResponse> {
    const response = await api.post('/auth/forgot-password', { email })
    return response.data
  },

  async resetPassword(data: any): Promise<ApiResponse> {
    const response = await api.post('/auth/reset-password', data)
    return response.data
  },

  async updateProfile(data: any): Promise<ApiResponse> {
    const response = await api.put('/auth/profile', data)
    return response.data
  },

  async getTokenUsage(): Promise<ApiResponse> {
    const response = await api.get('/auth/token-usage')
    return response.data
  },

  async getTokenHistory(params?: { projectId?: string, page?: number, limit?: number }): Promise<ApiResponse> {
    const response = await api.get('/auth/token-history', { params })
    return response.data
  },

  // ── MFA Methods ──────────────────────────────────────────────

  async getMfaStatus(): Promise<ApiResponse<{ mfaEnabled: boolean; hasSecret: boolean }>> {
    const response = await api.get('/auth/mfa/status')
    return response.data
  },

  async setupMfa(): Promise<ApiResponse<{ secret: string; qrCodeDataUrl: string; backupCodes: string[]; otpAuthUrl: string }>> {
    const response = await api.post('/auth/mfa/setup')
    return response.data
  },

  async verifyMfaSetup(code: string): Promise<ApiResponse> {
    const response = await api.post('/auth/mfa/verify-setup', { code })
    return response.data
  },

  async disableMfa(password: string, code: string): Promise<ApiResponse> {
    const response = await api.post('/auth/mfa/disable', { password, code })
    return response.data
  },

  async verifyMfa(mfa_token: string, code: string): Promise<ApiResponse> {
    const response = await api.post('/auth/mfa/verify', { mfa_token, code })
    return response.data
  },

  async verifyMfaBackup(mfa_token: string, backupCode: string): Promise<ApiResponse> {
    const response = await api.post('/auth/mfa/verify-backup', { mfa_token, backupCode })
    return response.data
  },
}

export default api